import { useState, useEffect, useRef, useCallback } from 'react';
import { router } from '@inertiajs/react';
import { cn } from '@/lib/utils';
import {
  isTabSwitch,
  isSamePageMutation,
  isBackgroundVisit,
  warnIfVisitShapeUnexpected,
} from '@/lib/tab-navigation';

const SHOW_DELAY_MS = 150;

export default function NavigationProgress() {
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const timerRef = useRef(null);
  const delayRef = useRef(null);
  const activeRef = useRef(false);

  const cleanup = useCallback(() => {
    clearTimeout(delayRef.current);
    clearInterval(timerRef.current);
  }, []);

  useEffect(() => {
    const removeStart = router.on('start', (event) => {
      // Dev tripwire: catch an @inertiajs upgrade that renames the prefetch/
      // async visit flags isBackgroundVisit depends on.
      warnIfVisitShapeUnexpected(event.detail.visit);
      // Prefetches and async partial reloads aren't navigations — no progress
      // bar. Filtered from `finish` too, so a background visit resolving
      // mid-delay can't cancel a real navigation's pending bar.
      if (isBackgroundVisit(event.detail.visit)) return;
      // Tab switches feel instant — no progress bar (iOS native behavior)
      if (isTabSwitch()) return;
      // Inline same-page mutations aren't navigations — no progress bar.
      if (isSamePageMutation(event.detail.visit)) return;

      cleanup();
      delayRef.current = setTimeout(() => {
        activeRef.current = true;
        setProgress(0);
        setVisible(true);

        let value = 0;
        timerRef.current = setInterval(() => {
          value += (90 - value) * 0.1;
          setProgress(value);
        }, 100);
      }, SHOW_DELAY_MS);
    });

    const removeFinish = router.on('finish', (event) => {
      if (isBackgroundVisit(event.detail.visit)) return;
      cleanup();

      if (activeRef.current) {
        activeRef.current = false;
        setProgress(100);
        setTimeout(() => {
          setVisible(false);
          setProgress(0);
        }, 200);
      }
    });

    return () => {
      removeStart();
      removeFinish();
      cleanup();
    };
  }, [cleanup]);

  if (!visible && progress === 0) return null;

  return (
    <div
      className={cn(
        'fixed inset-x-0 top-0 z-[100] h-0.5 transition-opacity duration-200',
        visible ? 'opacity-100' : 'opacity-0',
      )}
    >
      <div
        className="h-full bg-primary transition-[width] duration-200 ease-out"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
}
